import { ARQRModal } from "@/configurator/presentation/ARQRModal";
import { ConfiguratorActions } from "@/configurator/presentation/ConfiguratorActions";
import { ModelBadge } from "@/configurator/presentation/ModelBadge";
import { ModelToolbar } from "@/configurator/presentation/ModelToolbar";
import { OptionsPanel } from "@/configurator/presentation/OptionsPanel";
import { QuantityBar } from "@/configurator/presentation/QuantityBar";
import { ZoomControls } from "@/configurator/presentation/ZoomControls";
import { SceneRenderer } from "@/configurator/presentation/renderers/SceneRenderer";
import { TwoDRenderer } from "@/configurator/presentation/renderers/TwoDRenderer";
import { useConfiguratorComponent } from "@/configurator/presentation/useConfiguratorComponent";
import type { RendererKind } from "@/configurator/domain/models/types";

type Props = {
  renderer?: RendererKind;
};

export function Configurator(props: Props) {
  const {
    title,
    renderer,
    hasChrome,
    fontFamily,
    activeModelPath,
    sceneRef,
    ar,
    onDownloadPDF,
    onDownloadModel,
    onDownloadImage,
  } = useConfiguratorComponent(props.renderer);

  const is3D = renderer !== "2d";

  return (
    <div
      className={[
        "configurator relative w-full overflow-hidden",
        hasChrome
          ? "rounded-2xl border border-slate-200 bg-white shadow-sm"
          : "configurator--frameless",
      ].join(" ")}
      style={{ fontFamily }}
    >
      {/* Two elements on purpose: `__layout` is the query container, `__grid` is
          what the container queries style. A container cannot match a query
          against itself, so the element that switches between stacked and
          side-by-side has to sit inside it. */}
      <div className="configurator__layout">
        <div className="configurator__grid">
          <div
            className={[
              "configurator__viewer overflow-hidden",
              // Radii are container-query driven (see `configurator.css`), not
              // Tailwind `md:` utilities: those key off the viewport, which says
              // nothing about how wide the widget itself is on a host page.
              hasChrome ? "configurator__viewer--chrome bg-white" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <ModelBadge />

            {is3D ? (
              /* Keyed by the model: a model swap recreates the scene at its
                 default framing, and the remount resets the zoom step with it. */
              <ZoomControls key={activeModelPath} sceneRef={sceneRef} />
            ) : null}

            {is3D ? (
              <ModelToolbar
                arStatus={ar.arStatus}
                arError={ar.arError}
                onOpenAR={ar.openAR}
                onDownloadModel={onDownloadModel}
                onDownloadImage={onDownloadImage}
              />
            ) : null}

            <div className="configurator__renderer">
              {is3D ? (
                <SceneRenderer onSceneReady={ar.onSceneReady} />
              ) : (
                <TwoDRenderer />
              )}
            </div>
          </div>

          <div className="configurator__panel flex flex-col">
            <div className="configurator__body">
              <div className="configurator__title">{title}</div>

              <OptionsPanel />

              <QuantityBar />
            </div>

            <ConfiguratorActions onDownloadPDF={onDownloadPDF} />
          </div>
        </div>
      </div>

      {ar.shareUrl ? (
        <ARQRModal url={ar.shareUrl} onClose={ar.closeShare} />
      ) : null}
    </div>
  );
}
